import { EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { Comparable } from './comparable';

export abstract class AbstractForm {
    @Output() eventEmitter: EventEmitter<AbstractForm|any> = new EventEmitter<AbstractForm|any>(true); 

    formGroup: FormGroup;
    loading: boolean = true;

    constructor(
        protected formBuilder: FormBuilder,
        protected logger: LoggerService,
        protected init : boolean = true
    ) {
        if(init){
            this.formDefault();
        }
    }

    formDefault(){
        this.formGroup = this.buildFormGroup();
        this.markAllTouched();
        this.eventEmitter.emit(this);
    }
    
    buildFormGroup(): FormGroup {
        return this.formBuilder.group([]);
    }

    endLoading(msg: any = null){
        this.loading = false;
    }

    isValid(){
        return this.formGroup.valid;
    }

    isDirty(){
        return this.formGroup.dirty;
    }

    /**
     * Recebe eventos vindos de outro formulário ou serviço
     * @param msg Objeto com as informações do evento
     */
    next(msg: any) {
        this.logger.debug([this, 'next', msg]);
        this.updateControls();
    }

    compareFn(c1: any, c2: any): boolean {
        if (c1 instanceof Comparable) {
            return c1.compareTo(c2) === 0;
        }
        if (c2 instanceof Array)
            return !!c2.filter(item => item === c1).length;

        return c1?.id && c2?.id? c1.id === c2.id : c1 === c2;
    }

    compareFnState(c1: any, c2: any): boolean {
        if(c2 === null)
          return false;
    
        return c1.state_id == c2.state_id || c1.state_id == c2;
      }
    

    /**
     * Deve atualizar os campos do formulário.
     */
    abstract updateControls(): void;

    /**
     * Deve atualizar o objeto com os dados inseridos no formulário.
     */
    abstract updateObject(): any;

    showCheckbox(controlName: string): boolean {
        return (!!this.getControl(controlName).value);
    }

    hasError(controlName: string, formGroup:FormGroup = this.formGroup): boolean{
        return (this.getControl(controlName, formGroup)?.invalid);
    }
    
    getErrorMessage(controlName: string, formGroup:FormGroup = this.formGroup): string {
        const control = this.getControl(controlName,formGroup);
        
        if (control.errors?.required) {
            return 'Obrigatório'; 
        }

        if (control.errors?.invalid) {
            return control.errors.msg;
        }
        return "Campo inválido";
    }

    protected getControl(controlName: string, formGroup : FormGroup = this.formGroup): AbstractControl {
        return formGroup?.controls[controlName];
    }

    getFormControl(controlName: string, formGroup : FormGroup = this.formGroup): any {
        return formGroup?.controls[controlName];
    }

    protected getControlValue(controlName: string, formGroup : FormGroup = this.formGroup): any {
        return this.getControl(controlName, formGroup)?.value;
    }

    protected setControlValue(controlName: string, value: any, formGroup : FormGroup = this.formGroup, options = {}) {
        if(!this.getControl(controlName, formGroup))
            return;
            
        let oldValue = this.getControlValue(controlName, formGroup);

        if (oldValue != value) {
            this.getControl(controlName, formGroup).setValue(value,options);
            this.getControl(controlName, formGroup).markAsDirty();
        }
    }

    setFormValues(data, formGroup : FormGroup = this.formGroup, options = {}){
        for(const field in data){
            if(data.hasOwnProperty(field) && formGroup.controls.hasOwnProperty(field)){
                if(formGroup.controls[field] instanceof FormGroup){
                    this.setFormValues(data[field], this.getFormControl(field, formGroup), options);
                } else {
                    this.setControlValue(field, data[field], formGroup, options);
                }
                    
            }
        }
    }

    getFormValues(formGroup : FormGroup = this.formGroup, onlyNotDisabled = false, onlyDirty = false): any{
        let values = {};
        if(formGroup?.controls)
        for(const field in formGroup?.controls)
            if(formGroup.controls.hasOwnProperty(field) && (!onlyNotDisabled || !this.getControl(field, formGroup).disabled) && (!onlyDirty || this.getControl(field, formGroup).dirty)){
                if(this.getFormControl(field,formGroup) instanceof FormGroup)
                    values[field] = this.getFormValues(this.getFormControl(field,formGroup), onlyNotDisabled, onlyDirty);
                else
                    values[field] = this.getControlValue(field, formGroup);
            }
        return values;
    }
/*
    enableEdit(enable: boolean = true, ignoreControl: string[] = [], alwaysDisableControl: string[] = [], onlyControls: string[] = []) {
        if(this.formGroup?.controls)
        Object.keys(this.formGroup?.controls).forEach((controlName: string) => {
            if (ignoreControl.indexOf(controlName) > -1 || (!!onlyControls?.length && onlyControls.indexOf(controlName) == -1)) {
                return;
            }

            const control = this.getControl(controlName);
            if (enable && alwaysDisableControl.indexOf(controlName) == -1) {
                control.enable();
            } else {
                control.disable();
            }
        });
    }
*/

    enableEdit(enable: boolean = true, ignoreControl: string[] = [], alwaysDisableControl: string[] = [], formGroup : FormGroup = this.formGroup) {
        for(const controlName in formGroup.controls){
            if(formGroup.controls.hasOwnProperty(controlName)){
                if (ignoreControl.indexOf(controlName) == -1 ) {
                    
                    const control = this.getFormControl(controlName,formGroup);

                    if(control instanceof FormGroup){
                        this.enableEdit(enable,ignoreControl, alwaysDisableControl, control);       
                    } else {
                        if (enable && alwaysDisableControl.indexOf(controlName) == -1) {
                            control.enable({});
                        } else {
                            control.disable();
                        }
                    }
                }
            }
        }
    }

    isDisabled(controlName){
        return this.formGroup?.controls[controlName]?.disabled;
    }

    setDirty(dirty: boolean = true, ignoreControl: string[] = []) {
        Object.keys(this.formGroup?.controls).forEach((controlName: string) => {
            if (ignoreControl.indexOf(controlName) > -1 ) {
                return;
            }

            const control = this.getControl(controlName);
            if (dirty) {
                control.markAsDirty();
            } else {
                control.markAsPristine();
            }
        });
    }

    markAllTouched( formGroup : FormGroup = this.formGroup){
        formGroup.markAllAsTouched();
        for(const controlName in formGroup.controls){
            const control = this.getFormControl(controlName,formGroup);

            if(control instanceof FormGroup){
                this.markAllTouched(control);
            }
        }
    }

    
    toLower(e){
        e.target.value = e.target.value.toLowerCase();
    }

}